import React, { useState, useRef, useEffect,useMemo } from 'react';
import { Box, TextField, Button, Typography, CircularProgress, Menu, MenuItem } from '@mui/material';

function isCode(text) {
  return (
    text.includes('```') ||
    /function |const |let |var |def |class |import |public |private |#include/.test(text)
  );
}

function downloadAsTxt(content, filename = 'output.txt') {
  const blob = new Blob([content], { type: 'text/plain' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function downloadAsPdf(content, filename = 'output.pdf') {
  // Dynamically import jsPDF if not already imported
  import('jspdf').then(jsPDFModule => {
    const doc = new jsPDFModule.jsPDF();
    
    // Set smaller font size
    doc.setFontSize(11);
    
    // Page dimensions
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const margin = 15;
    const lineHeight = 7;
    const maxWidth = pageWidth - 2 * margin;
    
    // Split text into lines that fit the page width
    const lines = doc.splitTextToSize(content, maxWidth);
    
    let yPosition = margin;
    
    lines.forEach((line, index) => {
      // Check if we need a new page
      if (yPosition + lineHeight > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }
      
      // Add the line to the current page
      doc.text(line, margin, yPosition);
      yPosition += lineHeight;
    });
    
    doc.save(filename);
  });
}

const Chatbot = () => {
  const [messages, setMessages] = useState(() => {
    const savedMessages = sessionStorage.getItem('chatbot-messages');
    return savedMessages 
      ? JSON.parse(savedMessages) 
      : [{ from: 'bot', text: 'Hello! How can I help you? You can also upload a file.' }];
  });

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [sessionId, setSessionId] = useState(() => {
    return sessionStorage.getItem('chatbot-sessionId') || null;
  });
  
  const [currentResponse, setCurrentResponse] = useState('');
  const [lastGeneratedFilename, setLastGeneratedFilename] = useState(null);
  const [lastS3Url, setLastS3Url] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const fileInputRef = useRef(null);

  // Hard-coded API URL
  const apiUrl = "https://api.elevate.de.ltim.io";

  // Encouragement messages for loader
  const encouragementMessages = useMemo(() => [
    "Stay tuned...",
    "Preparing your answer...",
    "Good things take time, please wait...",
    "Generating something awesome for you..."
  ], []);

  const [loaderMessage, setLoaderMessage] = useState(encouragementMessages[0]);
  const [loaderIndex, setLoaderIndex] = useState(0);

  // Save chat history and sessionId in sessionStorage
  useEffect(() => {
    sessionStorage.setItem('chatbot-messages', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (sessionId) {
      sessionStorage.setItem('chatbot-sessionId', sessionId);
    }
  }, [sessionId]);

  // Rotate loader messages
  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setLoaderIndex(prev => {
          const newIndex = (prev + 1) % encouragementMessages.length;
          setLoaderMessage(encouragementMessages[newIndex]);
          return newIndex;
        });
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading, encouragementMessages]);

  // Download file function (always download in same tab)
  // const downloadFile = (filename, s3Url = null) => {
  //   if (!filename && !s3Url) return;
  //   let downloadUrl = s3Url ? s3Url : `${apiUrl}/download/${filename}`;
  //   const link = document.createElement('a');
  //   link.href = downloadUrl;
  //   link.download = filename || 'download';
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };


  // Send message with streaming support
  const sendMessage = async () => {
    if (!input.trim()) return;
    
    setLoading(true);
    const userMessage = input;
    setMessages(prev => [...prev, { from: 'user', text: userMessage }]);
    setInput('');
    setCurrentResponse('');

    try {
      const chatEndpointUrl = `${apiUrl}/chat`;
      const formData = new FormData();
      formData.append('message', userMessage);
      if (sessionId) {
        formData.append('session_id', sessionId);
      }
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const response = await fetch(chatEndpointUrl, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        let errorText = "";
        try {
          const errorData = await response.json();
          errorText = JSON.stringify(errorData);
        } catch {
          errorText = await response.text();
        }
        setMessages(prev => [...prev, { from: 'error', text: `Request failed: ${response.status} ${errorText}` }]);
        setLoading(false);
        return;
      }
      
      // Read the streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let responseText = "";
      let firstChunkReceived = false;

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          setLoading(false);
          break;
        }
        const chunk = decoder.decode(value);

        if (!firstChunkReceived) {
          setLoading(true);
          firstChunkReceived = true;
        }
        
        try {
          const lines = chunk.split("\n");
          let jsonString = "";
          let collectingJson = false;
          
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();
            if (line.startsWith("data: {")) {
              jsonString = line.substring(6);
              collectingJson = true;
            } 
            else if (collectingJson && line.startsWith("data:")) {
              jsonString += line.substring(5);
            }
            else if (collectingJson && line === "") {
              collectingJson = false;
              try {
                const jsonData = JSON.parse(jsonString);
                if (jsonData.status === "chunk") {
                  responseText += jsonData.content;
                  setCurrentResponse(responseText);
                  setMessages(prev => {
                    const newMessages = [...prev];
                    if (
                      newMessages.length === 0 ||
                      !newMessages[newMessages.length - 1] ||
                      newMessages[newMessages.length - 1].from !== 'bot'
                    ) {
                      newMessages.push({ from: 'bot', text: responseText });
                    } else {
                      newMessages[newMessages.length - 1] = { ...newMessages[newMessages.length - 1], text: responseText };
                    }
                    return newMessages;
                  });
                } 
                else if (jsonData.status === "complete") {
                  if (jsonData.session_id) {
                    setSessionId(jsonData.session_id);
                  }
                  if (jsonData.filename) {
                    setLastGeneratedFilename(jsonData.filename);
                    setLastS3Url(jsonData.s3_url || null);
                    setMessages(prev => [...prev, { 
                      from: 'system', 
                      text: 'Download Output From Here 👉', 
                      filename: jsonData.filename,
                      s3Url: jsonData.s3_url,
                      type: jsonData.type,
                      content: responseText
                    }]);
                  }
                 
                }
                else if (jsonData.status === "failed" || jsonData.status === "error") {
                  setMessages(prev => [...prev, { from: 'error', text: jsonData.message }]);
                }
              } catch (e) {
                console.error("Error parsing JSON:", e.message);
              }
              jsonString = "";
            }
          }
        } catch (e) {
          console.error("Error processing chunk:", e.message);
        }
      }
      setSelectedFile(null);
    } catch (error) {
      setMessages(prev => [...prev, { from: 'error', text: `Request failed: ${error.message}` }]);
      setLoading(false);
    }
  };

  // Handle file change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setSelectedFile(file);
    e.target.value = '';
  };

  // Delete selected file
  const handleDeleteFile = () => {
    setSelectedFile(null);
  };

  const clearMemory = async () => {
    if (!window.confirm("Are you sure you want to clear all memory? This will delete all conversation and output files.")) return;
    try {
      const response = await fetch(`${apiUrl}/clear_memory`, { method: "POST" });
      const data = await response.json();
      const defaultMessages = [{ from: 'bot', text: 'Hello! How can I help you? You can also upload a file.' }];
      setMessages(defaultMessages);
      sessionStorage.setItem('chatbot-messages', JSON.stringify(defaultMessages));
      sessionStorage.removeItem('chatbot-sessionId');
      setSelectedFile(null);
      setSessionId(null);
      setLastGeneratedFilename(null);
      setLastS3Url(null);
    } catch (e) {
      setMessages(prev => [...prev, { from: 'error', text: `Failed to clear memory: ${e.message}` }]);
    }
  };

  const getBoxWidth = (text) => {
    const minWidth = 200;
    const maxWidth = 500;
    const approxCharWidth = 10;
    const lines = text.split('\n');
    const longestLine = Math.max(...lines.map(line => line.length));
    const width = Math.min(Math.max(longestLine * approxCharWidth + 40, minWidth), maxWidth);
    return width;
  };

  // Handle dropdown menu
  const handleDownloadClick = (event, message) => {
    setAnchorEl(event.currentTarget);
    setSelectedMessage(message);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMessage(null);
  };

  const handleDownloadTxt = () => {
    if (selectedMessage) {
      if (selectedMessage.type === "code" && selectedMessage.content) {
        downloadAsTxt(selectedMessage.content, 'output.txt');
      } else if (selectedMessage.content) {
        downloadAsTxt(selectedMessage.content, 'output.txt');
      } else {
        downloadAsTxt(selectedMessage.text, 'output.txt');
      }
    }
    handleMenuClose();
  };

  const handleDownloadPdf = () => {
    if (selectedMessage) {
      if (selectedMessage.type === "non-code" && selectedMessage.content) {
        downloadAsPdf(selectedMessage.content, 'output.pdf');
      } else if (selectedMessage.content) {
        downloadAsPdf(selectedMessage.content, 'output.pdf');
      } else {
        downloadAsPdf(selectedMessage.text, 'output.pdf');
      }
    }
    handleMenuClose();
  };


  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '1300px', 
      height: '90vh',           
      display: 'flex', 
      flexDirection: 'column', 
      mx: 'auto',              
      mt: -7,                    
      bgcolor: 'transparent',
      p: 1                      
    }}>
      {/* Header */}
      <Typography 
        variant="h4" 
        sx={{ 
          fontFamily: 'Bahnschrift',
          mb: 2,
          textAlign: 'center',
          color: '#002147',
          pt: 1,
        }}
      >
        Elevate AI Bot
      </Typography>
      
      {/* Chat Messages Area */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        mb: 0,
        minHeight: '60vh',
        maxHeight: '20vh',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        p: 2,
        backgroundColor: '#fafafa'
      }}>
        {messages.map((msg, idx) => {
  const isBackendOutput = msg.from === 'system' && msg.text && idx !== 0;
  const isCodeOutput = isBackendOutput && isCode(msg.text);
  //const isNonCodeOutput = isBackendOutput && !isCode(msg.text);
 
  return (
    <Box
      key={idx}
      sx={{
        display: 'flex',
        justifyContent: msg.from === 'user' ? 'flex-end' : 'flex-start',
        mb: 2,
      }}
      >
      <Box
        sx={{
          bgcolor: msg.from === 'user' ? '#e3f2fd' : 
                   msg.from === 'error' ? '#ffebee' :
                   msg.from === 'system' ? '#f3e5f5' : '#ffffff',
          border: '1px solid #ddd',
          borderRadius: 2,
          px: 3,
          py: 2,
          maxWidth: '80%',
          minWidth: getBoxWidth(msg.text),
          fontFamily: msg.from === 'bot' ? 'Ebrima' : 'inherit',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
         >
         <Typography
          variant="body1"
          sx={{
            fontFamily: msg.from === 'bot' ? 'monospace' : 'inherit',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: msg.from === 'error' ? 'red' : 
                   msg.from === 'system' ? 'purple' : 'inherit',
            flexGrow: 1
          }}
         >
          {msg.text}
        </Typography>
        {/* Download file button for backend output */}
        {msg.filename && (
          <Button
            variant="contained"
            size="small"
            onClick={(e) => handleDownloadClick(e, msg)}
            sx={{
              backgroundColor: '#4CAF50',
              color: 'white',
              '&:hover': {
                backgroundColor: '#45a049',
              },
              flexShrink: 0
            }}
          >
            Download File
          </Button>
        )}
 
        {/* Download TXT for code output */}
        {isCodeOutput && (
         <Button
            variant="outlined"
            size="small"
            onClick={(e) => handleDownloadClick(e, msg)}
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              ml: 1,
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: '#e3f2fd'
              },
              flexShrink: 0
            }}
            >
            Download
         </Button>
          )}
          </Box>
          </Box>
          );
         })}
        
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'center' }}>
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#888' }}>
              {loaderMessage}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Dropdown Menu for Download Options */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
          },
        }}
      >
        <MenuItem 
          onClick={handleDownloadTxt}
          sx={{
            '&:hover': {
              backgroundColor: '#e3f2fd',
            },
          }}
        >
          Download as TXT
        </MenuItem>
        <MenuItem 
          onClick={handleDownloadPdf}
          sx={{
            '&:hover': {
              backgroundColor: '#e3f2fd',
            },
          }}
        >
          Download as PDF
        </MenuItem>
      </Menu>

      {/* Input Area */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Type your message..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          disabled={loading}
          multiline
          maxRows={3}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'white'
            }
          }}
        />
        <Button 
          variant="contained"
          onClick={sendMessage} 
          disabled={loading || !input.trim()}
          sx={{
            backgroundColor: 'rgb(1 63 114)',
            color: 'white',
            height: '56px',
            px: 4,
            fontSize: '16px',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#1565c0'
            }
          }}>
          SEND
        </Button>
      </Box>

      {/* Control Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <input
          type="file"
          hidden
          ref={fileInputRef}
          onChange={handleFileChange}
          disabled={loading}
          accept=".txt,.csv,.xls,.xlsx,.doc,.docx,.sql,.py,.js,.json"
        />
        <Button
  variant="outlined"
  onClick={() => fileInputRef.current && fileInputRef.current.click()}
  disabled={loading}
  sx={{
    backgroundColor: 'white',
    color: 'black',
    border: '1px solid black',
    boxShadow: 'none',
    height: '40px',
    borderColor: 'rgb(1 63 114)',
    '&:hover': {
      backgroundColor: '#e6f0fa', // very light blue
      borderColor: 'rgb(1 63 114)',
    }
  }}
>
  UPLOAD FILE
</Button>
        
        {selectedFile && (
          <Typography variant="body2" sx={{ color: 'rgb(1 63 114)', fontWeight: 'medium' }}>
            🔗{selectedFile.name}
          </Typography>
        )}
        
        <Button
          variant="outlined"
          onClick={handleDeleteFile}
          disabled={!selectedFile}
          sx={{
            borderColor: '#f44336',
            color: '#f44336',
            '&:hover': {
              borderColor: '#d32f2f',
              backgroundColor: '#fff5f5'
            }
          }}
        >
          DELETE FILE
        </Button>
        
        <Button
  variant="outlined"
  onClick={clearMemory}
  sx={{
    backgroundColor: 'white',
    color: 'black',
    border: '1px solid black',
    boxShadow: 'none',
    height: '40px',
    borderColor: 'rgb(1 63 114)',
    '&:hover': {
      backgroundColor: '#e6f0fa', // very light blue
      borderColor: 'rgb(1 63 114)',
    }
  }}
>
  CLEAR HISTORY
</Button>
      </Box>
    </Box>
  );
};

export default Chatbot;